import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Plus, ThumbsUp, ChevronDown, Headphones, Eye } from 'lucide-react';
import { Button } from './ui/button';

// Use current origin for deployed environment
const getAssetBaseUrl = () => window.location.origin;

// Build cover URL - handles both old filesystem URLs and new database URLs
const getCoverUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/192x288?text=No+Cover';
  // If it's already an absolute URL, use as-is
  if (url.startsWith('http')) return url;
  // For relative URLs (/api/files/... or /api/db-files/...), use current origin
  if (url.startsWith('/api')) {
    return `${getAssetBaseUrl()}${url}`;
  }
  return url;
};

const BookCard = ({ book, isAudiobook = false, viewCount }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Check if this is an audiobook
  const hasAudio = isAudiobook || book.is_audiobook || book.audiobook_chapters?.length > 0;

  const coverUrl = getCoverThumbUrl(book.cover_image_url || book.coverImage);

  return (
    <div
      className="relative flex-shrink-0 w-48 cursor-pointer transition-all duration-300 ease-out"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(`/book/${book.id}`)}
      style={{
        transform: isHovered ? 'scale(1.15)' : 'scale(1)',
        zIndex: isHovered ? 20 : 1,
      }}
    >
      {/* Book Cover */}
      <div className={`relative rounded-md overflow-hidden group ${hasAudio ? 'ring-2 ring-purple-500/50' : ''}`}>
        <div className="w-full h-72 bg-gray-800 flex items-center justify-center">
          <img
            src={coverUrl}
            alt={book.title}
            className="w-full h-full object-contain transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/192x288?text=No+Cover';
            }}
          />
        </div>
        
        {/* Audiobook Badge */}
        {hasAudio && !isHovered && (
          <div className="absolute top-2 right-2 bg-purple-600/90 backdrop-blur-sm rounded-full p-1.5">
            <Headphones className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Chapter Count Badge */}
        {book.audiobook_chapters?.length > 1 && !isHovered && (
          <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5 text-xs text-purple-300">
            {book.audiobook_chapters.length} chapters
          </div>
        )}

        {/* View Count Badge */}
        {viewCount > 0 && !isHovered && (
          <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] text-white flex items-center gap-1">
            <Eye className="w-3 h-3 text-blue-400" /> {viewCount}
          </div>
        )}
        
        {/* Hover Overlay */}
        {isHovered && (
          <div className={`absolute inset-0 animate-fade-in ${hasAudio ? 'bg-gradient-to-t from-purple-900/90 via-black/60 to-transparent' : 'bg-gradient-to-t from-black via-black/50 to-transparent'}`}>
            <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <Button
                  size="icon"
                  className={`h-8 w-8 rounded-full ${hasAudio ? 'bg-purple-500 hover:bg-purple-400' : 'bg-white hover:bg-white/90'} text-white`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/book/${book.id}`);
                  }}
                >
                  {hasAudio ? <Headphones className="h-4 w-4" /> : <Play className="h-4 w-4 fill-current text-black" />}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-full border-2 border-white/60 text-white hover:border-white hover:bg-white/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-full border-2 border-white/60 text-white hover:border-white hover:bg-white/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ThumbsUp className="h-4 w-4" />
                </Button>
                <div className="flex-1" />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-full border-2 border-white/60 text-white hover:border-white hover:bg-white/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              {/* Book Info */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-xs text-white">
                  <span className="text-yellow-400 font-semibold">★ {book.rating || 0}</span>
                  <span>•</span>
                  <span>{book.publish_year || book.publishYear}</span>
                  {hasAudio && book.audiobook_chapters?.length > 0 && (
                    <>
                      <span>•</span>
                      <span className="text-purple-300">{book.audiobook_chapters.length} ch</span>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {book.genres && book.genres.slice(0, 2).map((genre, index) => (
                    <span
                      key={index}
                      className={`text-xs px-2 py-0.5 rounded ${hasAudio ? 'text-purple-200 bg-purple-500/30' : 'text-white/80 bg-white/10'}`}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Title (always visible) */}
      {!isHovered && (
        <div className="mt-2 px-1">
          <h3 className={`text-sm font-medium truncate ${hasAudio ? 'text-purple-200' : 'text-white'}`}>{book.title}</h3>
          <p className="text-xs text-gray-400 truncate">{book.author}</p>
        </div>
      )}
    </div>
  );
};

export default BookCard;